"use client";

import type React from "react";
import { Upload, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import type { SettingsData } from "@/services/types";

type OpportunityFormData = {
  title: string;
  summary: string;
  media: string;
  isExternal: boolean;
  url?: string;
};

interface StepOneFormProps {
  register: UseFormRegister<OpportunityFormData>;
  errors: FieldErrors<OpportunityFormData>;
  setValue: UseFormSetValue<OpportunityFormData>;
  watch: UseFormWatch<OpportunityFormData>;
  mediaFile: File | null;
  mediaPreview: string;
  onMediaChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMedia: () => void;
  onClearUrl: () => void;
  isUploadingMedia?: boolean;
  settings: SettingsData;
  setSettings: React.Dispatch<React.SetStateAction<SettingsData>>;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
}

export function StepOneForm({
  register,
  errors,
  // setValue,
  watch,
  mediaFile,
  mediaPreview,
  onMediaChange,
  onRemoveMedia,
  onClearUrl,
  isUploadingMedia = false,
  settings,
  setSettings,
  status,
  setStatus,
}: StepOneFormProps) {
  const urlValue = watch("url");
  const isExternal = watch("isExternal");

  return (
    <>
      {/* Opportunity Title */}
      <div>
        <label className="block text-base font-semibold text-foreground mb-3">
          Opportunity Title <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="Enter title"
          {...register("title")}
          className="bg-muted/50 border-input"
        />
        {errors.title && (
          <p className="text-destructive text-sm mt-2">
            {errors.title.message}
          </p>
        )}
      </div>

      {/* Summary */}
      <div>
        <label className="block text-base font-semibold text-foreground mb-3">
          Summary <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Max 1000 characters.
        </p>
        <Textarea
          placeholder="Write something about the opportunity here"
          {...register("summary")}
          rows={6}
          className="bg-muted/50 border-input"
        />
        {errors.summary && (
          <p className="text-destructive text-sm mt-2">
            {errors.summary.message}
          </p>
        )}
      </div>

      {/* Cover Image */}
      <div>
        <label className="block text-base font-semibold text-foreground">
          Cover image <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-3">
          Accepted file type: JPG, PNG, GIF, or WebP. Do not upload a file
          larger than 3MB.
        </p>
        {mediaFile || mediaPreview ? (
          <div className="bg-muted/50 border border-input rounded-lg p-4">
            {mediaPreview && (
              <img
                src={mediaPreview || "/placeholder.svg"}
                alt="Media preview"
                className="w-full h-48 object-cover rounded-lg mb-3"
              />
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {mediaFile?.name || "Current media"}
                </p>
                {mediaFile && (
                  <p className="text-xs text-muted-foreground">
                    {(mediaFile.size / 1024).toFixed(2)} KB
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onRemoveMedia}
                className="text-destructive hover:opacity-80 transition-opacity"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <label
            className={`flex items-center justify-center gap-2 bg-muted/50 border border-input rounded-lg p-8 transition-colors ${
              isUploadingMedia
                ? "cursor-wait opacity-60"
                : "cursor-pointer hover:bg-muted/70"
            }`}
          >
            <Upload className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">
              {isUploadingMedia ? "Uploading..." : "Add media file"}
            </span>
            <input
              type="file"
              {...register("media")}
              onChange={onMediaChange}
              className="hidden"
              accept="image/*"
              disabled={isUploadingMedia}
            />
          </label>
        )}
      </div>

      {/* External Opportunity */}
      <div className="flex items-center gap-2">
        <input
          id="isExternal"
          type="checkbox"
          {...register("isExternal")}
          className="h-4 w-4 rounded border-input text-primary"
        />
        <label htmlFor="isExternal" className="text-sm font-medium">
          Is this an external opportunity ?
        </label>
      </div>

      {/* URL */}
      {isExternal && (
        <div>
          <label className="block text-base font-semibold text-foreground mb-3">
            URL <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              placeholder="https://example.com/opportunities/your-opportunity"
              {...register("url")}
              className="bg-muted/50 border-input pr-10"
            />
            {urlValue && (
              <button
                type="button"
                onClick={onClearUrl}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-destructive hover:opacity-80 transition-opacity"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
          {errors.url && (
            <p className="text-destructive text-sm mt-2">
              {errors.url.message}
            </p>
          )}
        </div>
      )}

      {isExternal && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Submission deadline
            </label>
            <Input
              type="datetime-local"
              value={
                settings.submissionDeadline
                  ? (() => {
                      const date = new Date(settings.submissionDeadline);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const day = String(date.getDate()).padStart(2, "0");
                      const hours = String(date.getHours()).padStart(2, "0");
                      const minutes = String(date.getMinutes()).padStart(2, "0");
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })()
                  : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                setSettings((prev) => ({
                  ...prev,
                  submissionDeadline: value ? new Date(value).toISOString() : "",
                }));
              }}
              className="bg-gray-50 h-12"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-muted/50 border-input h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </>
  );
}
